import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CapacityBadge, CtaNote, WhatsAppCta, easing } from "@/components/brand";
import BrowserShot from "@/components/BrowserShot";
import { CTA_NOTE, cases } from "@/lib/offer";

const enter = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: easing },
});

const secondLine = ["Seu", "site", "devia", "parecer"];

export default function Hero() {
  const figureRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: figureRef, offset: ["start end", "end start"] });
  const backY = useTransform(scrollYProgress, [0, 1], [24, -24]);
  const frontY = useTransform(scrollYProgress, [0, 1], [-16, 16]);
  const [main, secondary] = cases;

  return (
    <section id="hero" data-testid="hero-section" className="relative pt-28 pb-16 md:pt-32 lg:pb-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <CapacityBadge className="mb-6" />
          <h1 className="text-[2.1rem] font-bold leading-[1.05] tracking-[-0.04em] text-foreground sm:text-5xl lg:text-[3.5rem] xl:text-[4rem]">
            <motion.span {...enter(0)} className="block">
              Sua empresa é séria.
            </motion.span>
            <span className="block text-primary">
              {secondLine.map((word, i) => (
                <motion.span
                  key={word}
                  initial={{ opacity: 0, y: "0.4em", filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.6, delay: 0.25 + i * 0.08, ease: easing }}
                  className="inline-block"
                >
                  {word}
                  {i < secondLine.length - 1 && " "}
                </motion.span>
              ))}
              <motion.span
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 14, delay: 0.7 }}
                className="inline-block origin-bottom text-accent"
              >
                .
              </motion.span>
            </span>
          </h1>

          <motion.p
            {...enter(0.1)}
            className="mt-6 max-w-[34rem] text-lg leading-relaxed text-muted-foreground"
          >
            Sites e sistemas sob medida para empresas, com valor fechado antes de começar e site no
            ar em 7 dias.
          </motion.p>

          <motion.div {...enter(0.2)} className="mt-9">
            <div className="flex flex-col gap-3 sm:flex-row">
              <WhatsAppCta data-testid="hero-cta-primary" location="hero" />
              <a
                href="#precos"
                data-testid="hero-cta-secondary"
                className="inline-flex h-12 items-center justify-center rounded-lg border border-border px-6 text-base font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                Ver preços
              </a>
            </div>
            <CtaNote className="mt-4">{CTA_NOTE} Valor fechado antes de começar.</CtaNote>
          </motion.div>
        </div>

        <motion.figure
          ref={figureRef}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: easing }}
          className="relative lg:col-span-5"
        >
          <div className="relative pb-10 pl-0 sm:pl-12 lg:pl-8">
            <motion.div style={{ y: backY }}>
              <BrowserShot
                src={main.fullImage}
                alt={`Página do ${main.name}, projeto da Ambitie`}
                url={main.url}
                motion="auto"
                priority
                className="shadow-[0_30px_60px_-30px_rgba(17,26,51,0.35)]"
              />
            </motion.div>
            <motion.div
              style={{ y: frontY }}
              className="absolute bottom-0 left-0 hidden w-[46%] sm:block"
            >
              <BrowserShot
                src={secondary.image}
                alt={`Site da ${secondary.name}, projeto da Ambitie`}
                url={secondary.url}
                width={1440}
                height={1080}
                className="shadow-[0_24px_48px_-24px_rgba(17,26,51,0.4)]"
              />
            </motion.div>
          </div>
          <figcaption className="mt-6 text-sm text-muted-foreground sm:text-right">
            <a href="#cases" className="underline-offset-4 hover:text-primary hover:underline">
              {main.name} e {secondary.name}, projetos da Ambitie
            </a>
          </figcaption>
        </motion.figure>
      </div>
    </section>
  );
}
